/* eslint-disable max-len */
import React, { forwardRef, useEffect, useMemo, useState } from "react";

import styled from "styled-components";

import { colors, fontStyles } from "../../Theme";
import CircularLoader from "../circularLoader";

import CloseCircleGreenIcon from "../../../../common/icons/CloseCircleGreenIcon";
import CloseCircleRedIcon from "../../../../common/icons/CloseCircleRedIcon";

import { isNullOrUndefined } from "../../../../utils/appUtils";

const StyledInputWrapper = styled.div`
  display: inline-flex;
  max-height: 32px;
  min-height: 2rem;
  width: 100%;
  align-items: center;
  color: ${colors.Black};
  gap: -1px;
  align-self: stretch;
  border-radius: 2px;
  border: 1px solid;
  border-color: ${({ isError, isFocused, disabled, isWarning }) =>
    disabled
      ? "#e1e1e1"
      : isError
        ? "#E4001C"
        : isWarning
          ? "#FF9E19"
          : isFocused
            ? "#007672"
            : "#e1e1e1"};
  background-color: ${({ disabled, readOnly }) =>
    disabled || readOnly ? "#e1e1e1" : "#fff"};
  transition:
    border-color 0.3s,
    box-shadow 0.3s;
  box-shadow: ${({ disabled }) => (disabled ? "none" : "")};
  &:hover {
    box-shadow: ${({ isFocused, disabled }) =>
      isFocused
        ? "0px 0px 0px 4px rgba(0, 169, 224, 0.20)"
        : disabled
          ? "none"
          : "0px 0px 0px 4px rgba(0, 169, 224, 0.20)"};
  }
`;
const StyledInput = styled.input`
  width: 100%;
  height: -webkit-fill-available;
  background-color: ${colors.White};
  border: none;
  outline: none;
  padding: 4px 10px;
  ${fontStyles.diodrum14}
  &::placeholder {
    color: ${colors.Grey96};
  }
  &:disabled {
    background-color: ${colors.GreyE1};
    pointer-events: none;
    color: ${colors.Grey96};
  }
  &:read-only {
    background-color: ${colors.GreyE1};
  }
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
`;
const StyledInputText = styled.span`
  background-color: ${(props) => props.unitBgColor || colors.GreyF8};
  padding: 4px 10px;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${fontStyles.diodrum14};
  line-height: 1.3rem;
  color: ${({ disabled }) => (disabled ? colors.Grey96 : colors.Black)};
  border-left: 1px solid #e1e1e1;
`;

const StyledInputIconWrapper = styled.span`
  background-color: "transparent";
  padding: 4px 5px;
  ${fontStyles.diodrum14};
  color: ${({ disabled }) => (disabled ? colors.Grey96 : colors.Black)};
  opacity: ${({ disabled }) => (disabled ? "0" : "1")};
  cursor: pointer;
`;

const INPUT_FILTER_KEYS = ["ArrowUp", "ArrowDown"];
const INPUT_FILTER_KEYS_NUMBER = ["e", "E", "+", "-", ...INPUT_FILTER_KEYS];

const InputWithText = forwardRef((props, ref) => {
  const {
    isError,
    className,
    value,
    name,
    step,
    unitBgColor,
    min,
    max,
    isWarning,
    onChange,
    placeholder,
    inputText,
    onBlur,
    onFocus,
    onWheel,
    onScroll,
    disabled,
    defaultValue,
    type,
    id,
    onKeyDown,
    onKeyPress,
    showCancelIcon,
    onCancelIconClick,
    isLoading,
    tabIndex,
    minLength,
    maxLength,
    readOnly,
    validator,
    setIsValid,
  } = props;
  const [isFocused, setIsFocused] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);

  useEffect(() => {
    const sanitizedValue = isNullOrUndefined(value) ? "" : value;
    setCurrentValue(sanitizedValue);
  }, [value, isFocused]);

  const handleFocus = (e) => {
    setIsFocused(true);
    onFocus && onFocus(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    onBlur && onBlur(e);
  };

  const handleKeyPress = (event) => {
    if (onKeyPress) {
      onKeyPress(event);
    } else if (event.key === "Enter") {
      event.target?.blur();
    }
  };

  const validateInput = (value) => setIsValid(validator.test(value));

  const handleChange = (e) => {
    setCurrentValue(e.target.value);
    if (validator) validateInput(e.target.value);
    onChange && onChange(e);
  };

  const inpuerFilterKeys = useMemo(
    () =>
      type?.toLowerCase() === "number"
        ? INPUT_FILTER_KEYS_NUMBER
        : INPUT_FILTER_KEYS,
    [type],
  );

  const handleKeyDown = (event = {}) => {
    if (onKeyDown) {
      onKeyDown(event);
    } else {
      inpuerFilterKeys.includes(event?.key) && event.preventDefault();
    }
  };

  const handleWheel = (event) => {
    if (onWheel) {
      onWheel(event);
    } else {
      event.target.blur();
    }
  };

  return (
    <>
      <StyledInputWrapper
        className={className}
        isError={isError}
        isWarning={isWarning}
        isFocused={isFocused}
        disabled={disabled}
        readOnly={readOnly}
      >
        <StyledInput
          id={id}
          name={name}
          type={type}
          step={step}
          value={currentValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onWheel={handleWheel}
          onScroll={onScroll}
          onChange={handleChange}
          placeholder={placeholder}
          defaultValue={defaultValue}
          disabled={disabled}
          min={min}
          max={max}
          minLength={minLength}
          maxLength={maxLength}
          onKeyDown={handleKeyDown}
          onKeyPress={handleKeyPress}
          tabIndex={tabIndex}
          readOnly={readOnly}
          ref={ref}
          autoFocus={props.autoFocus}
          data-is-error={isError}
          validator={validator}
        />
        {inputText && (
          <StyledInputText disabled={disabled} unitBgColor={unitBgColor}>
            {inputText}
          </StyledInputText>
        )}

        {showCancelIcon && !isLoading && (
          <StyledInputIconWrapper onClick={onCancelIconClick}>
            {isError ? <CloseCircleRedIcon /> : <CloseCircleGreenIcon />}
          </StyledInputIconWrapper>
        )}

        {isLoading && (
          <CircularLoader className="loader" size="20px"></CircularLoader>
        )}
      </StyledInputWrapper>
    </>
  );
});

InputWithText.displayName = "InputWithText";

export default InputWithText;
